import { useTranslation } from "react-i18next";
import { Control, Controller } from "react-hook-form";
import { Checkbox, IconButton } from "@carbon/react";
import { TrashCan } from "@carbon/icons-react";
import TextField from "../carbon/Form/TextField";
import SelectField from "../carbon/Form/SelectField";
import { AddressesFormFields } from "./addressesValidationSchema";
import { CountryResponse } from "../../types";

export interface AddressRowProps {
  index: number;
  control: Control<AddressesFormFields>;
  countries: CountryResponse[];
  onRemove: () => void;
}

/**
 * One row of `AddressesForm`'s `useFieldArray` — the per-row markup Task 3's primitives gap-check
 * decided NOT to generalise into a shared wrapper (every repeatable sub-form's row content
 * diverges too much; see that task's report). `street`/`city`/`state`/`postalCode` (`TextField`)
 * and `countryId` (`SelectField`) use the array-indexed RHF path (`addresses.${index}.street`)
 * against the SAME `control` the parent form owns — no row-local state, no row-local `useForm`.
 *
 * `isLegalAddress`/`isDeliverySite` are Carbon `Checkbox`, bound via a bare `Controller` rather
 * than through `TextField`/`SelectField` (neither fits a checkbox) — this is the "compose
 * primitives per row" pattern Task 3's report calls out explicitly, not a new primitive.
 */
const AddressRow = ({
  index,
  control,
  countries,
  onRemove,
}: AddressRowProps) => {
  const { t } = useTranslation();

  const countryOptions = countries.map((country) => ({
    value: country.id,
    label: country.name,
  }));

  return (
    <div className="addresses-form__row">
      <div className="addresses-form__row-grid">
        <TextField
          name={`addresses.${index}.street`}
          label={t("companyProfile.street")}
          control={control}
        />
        <TextField
          name={`addresses.${index}.city`}
          label={t("companyProfile.city")}
          control={control}
        />
        <TextField
          name={`addresses.${index}.state`}
          label={t("companyProfile.state")}
          control={control}
        />
        <TextField
          name={`addresses.${index}.postalCode`}
          label={t("companyProfile.postalCode")}
          control={control}
        />
        <SelectField
          name={`addresses.${index}.countryId`}
          label={t("companyProfile.country")}
          control={control}
          options={countryOptions}
          placeholder={t("companyProfile.selectCountry")}
        />
      </div>

      <div className="addresses-form__row-flags">
        <Controller
          name={`addresses.${index}.isLegalAddress`}
          control={control}
          render={({ field }) => (
            <Checkbox
              id={`addresses.${index}.isLegalAddress`}
              labelText={t("companyProfile.legalAddress")}
              checked={Boolean(field.value)}
              onChange={(_event, { checked }) => field.onChange(checked)}
            />
          )}
        />
        <Controller
          name={`addresses.${index}.isDeliverySite`}
          control={control}
          render={({ field }) => (
            <Checkbox
              id={`addresses.${index}.isDeliverySite`}
              labelText={t("companyProfile.deliverySite")}
              checked={Boolean(field.value)}
              onChange={(_event, { checked }) => field.onChange(checked)}
            />
          )}
        />
        <IconButton
          kind="ghost"
          label={t("cta.remove")}
          align="top"
          onClick={onRemove}
        >
          <TrashCan />
        </IconButton>
      </div>
    </div>
  );
};

export default AddressRow;
